import type { CityConfig } from "@/pages/city-template/types";

export const peterboroughConfig: CityConfig = {
  city: "Peterborough",
  state: "New Hampshire",
  stateAbbr: "NH",
  slug: "peterborough",
  route: "/cities/peterborough",
  title: "Emergency & Expedited Passport Services Peterborough NH | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Peterborough, NH — as fast as 24 hours. No Peterborough passport agency. Fast Passport Center couriers to the Boston Passport Agency at Tip O'Neill Federal Building, about 85 miles southeast. A+ BBB rated. Serving Dublin, Jaffrey, Hancock, and all Monadnock Region travelers heading abroad for arts, business, or family.",
  h1: "Emergency & Expedited Passport Services in Peterborough, NH",
  heroSubtitle: "Peterborough is the cultural heart of New Hampshire's Monadnock Region — a vibrant Hillsborough County town known for the historic Peterborough Players theater, the MacDowell Colony artist residency, and a walkable downtown filled with independent bookshops, galleries, and cafés along the Contoocook River. Home to the iconic Peterborough Town House and a thriving arts community, Peterborough draws creative professionals, outdoor enthusiasts, and families seeking the best of New England small-town living. Fast Passport Center's registered couriers deliver your documents to the Boston Passport Agency at the Tip O'Neill Federal Building so you get a new or renewed passport as fast as 24 hours — whether you're a MacDowell fellow heading to an international residency, a Peterborough Players artist touring abroad, or a Monadnock Region traveler planning the journey of a lifetime overseas.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Peterborough%20New%20Hampshire%20downtown%20Main%20Street%20with%20historic%20Peterborough%20Town%20House%20clock%20tower%20and%20Contoocook%20River%20flowing%20through%20town%2C%20classic%20New%20England%20village%20with%20white%20church%20steeple%20and%20vibrant%20autumn%20foliage%2C%20wide%20angle%20photography%20with%20historic%20architecture%20and%20fall%20colors&width=1800&height=700&seq=peterborough-nh-hero-001&orientation=landscape",
  heroImageAlt: "Peterborough New Hampshire downtown with Town House clock tower and autumn foliage",
  geoPosition: "42.8706;-71.9517",
  geoPlacename: "Peterborough, New Hampshire",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "Tip O'Neill Federal Building, 10 Causeway Street, Boston, MA 02222",
    distance: "Approximately 85 miles from Peterborough — about 1 hour 30 minutes via NH-101 and I-93 south",
  },
  nearbyCitiesZips: [
    { name: "Peterborough", zips: "03458" },
    { name: "Dublin", zips: "03444" },
    { name: "Jaffrey", zips: "03452" },
    { name: "Hancock", zips: "03449" },
    { name: "Sharon", zips: "03458" },
    { name: "Temple", zips: "03084" },
    { name: "Greenfield", zips: "03047" },
    { name: "Bennington", zips: "03442" },
    { name: "Antrim", zips: "03440" },
    { name: "Wilton", zips: "03086" },
    { name: "New Ipswich", zips: "03071" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Boston Passport Agency — about 85 miles from Peterborough via NH-101 and I-93" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Monadnock Region Service", desc: "Serving Peterborough, Dublin, Jaffrey, Hancock, and all eastern Monadnock communities" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Peterborough case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in New Hampshire", route: "/states/new-hampshire" },
    { name: "Expedited passport in Manchester NH", route: "/cities/manchester" },
    { name: "Urgent passport in Peterborough NH", route: "/cities/peterborough" },
    { name: "New Hampshire passport hub", route: "/states/new-hampshire" },
    { name: "Fast passport in Keene NH", route: "/cities/keene" },
    { name: "Expedited passport in Jaffrey NH", route: "/cities/jaffrey" },
    { name: "Emergency passport in Concord NH", route: "/cities/concord-nh" },
  ],
  fpcOfficeAddress: "Tip O'Neill Federal Building, 10 Causeway Street, Boston, MA 02222",
  fpcOfficeNote: "Fast Passport Center serves Peterborough remotely via secure courier. Our registered couriers hand-deliver your documents to the Boston Passport Agency at the Tip O'Neill Federal Building — approximately 85 miles southeast. Peterborough's location along NH-101 and the I-93 corridor makes Boston courier routing straightforward and reliable year-round. Our dedicated agents ensure Monadnock Region travelers, from MacDowell Colony artists to Peterborough Players performers, never need to navigate the drive to Boston for passport processing.",
  fpcOfficeDistance: "Serving all of Hillsborough County's eastern Monadnock Region and the Peterborough area",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Peterborough, Dublin, Jaffrey, Hancock, and the entire eastern Monadnock Region. From MacDowell Colony artists traveling to international residencies to Contoocook Valley families heading abroad, we help Peterborough travelers get their passports fast.",
  nearbyCitiesIntro: "We serve Peterborough and nearby communities including Dublin, Jaffrey, Hancock, Sharon, Temple, Greenfield, Bennington, Antrim, Wilton, and New Ipswich — plus the greater Contoocook Valley and Monadnock Region.",
  howItWorksIntro: "Most Peterborough travelers complete everything online and ship documents overnight. At approximately 85 miles from Boston, Fast Passport Center provides fast courier turnaround for Monadnock Region residents — eliminating the 1 hour 30 minute drive through NH-101 and I-93 to Boston.",
  mistakesIntro: "Peterborough travelers, including MacDowell Colony artists and Monadnock Region professionals, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "Peterborough and Monadnock Region travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Peterborough? Here are the most common ones we hear from Monadnock Region and Hillsborough County travelers.",
  statePageRoute: "/states/new-hampshire",
  statePageName: "New Hampshire",
  dmvWebsite: "https://www.nh.gov/safety/divisions/dmv/index.htm",
  dmvLabel: "New Hampshire DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at Causeway Street",
        "Life-or-death emergencies only",
        "85 miles to Boston (1 hour 30 minutes via NH-101/I-93)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for MacDowell Colony artists",
        "Perfect for Peterborough Players",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from BOS or MHT",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Peterborough is 85 miles from the Boston Passport Agency — our courier service saves Monadnock Region travelers the drive through NH-101 and Boston traffic.",
      bullets: ["Hillsborough County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in the Monadnock Region",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Peterborough adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Peterborough and eastern Monadnock"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Peterborough and Hillsborough County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Peterborough and Monadnock Region residents with DS-64 form guidance and courier routing to Boston.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Boston agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Hillsborough County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Peterborough and Hillsborough County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Peterborough NH", description: "Urgent passport processing for Peterborough, NH travelers via Boston Passport Agency courier — 85 miles from Peterborough." },
    { name: "Passport Renewal Peterborough NH", description: "Fast passport renewal for Peterborough and Hillsborough County, New Hampshire residents." },
    { name: "New Passport Peterborough NH", description: "First-time passport application assistance for Peterborough and Monadnock Region area residents." },
    { name: "Lost Passport Peterborough NH", description: "Passport replacement for lost or stolen passports for Peterborough and Hillsborough County residents." },
    { name: "Child Passport Peterborough NH", description: "Minor passport services for Peterborough and Monadnock Region families." },
    { name: "Name Change Passport Peterborough NH", description: "Passport name change service for Peterborough, NH residents." },
  ],
  testimonials: [
    {
      name: "Elena V.",
      location: "Peterborough, NH — MacDowell Colony Fellow",
      rating: 5,
      text: "I'm a painter and was accepted to a 6-week residency in Provence, France through the MacDowell network. Realized my passport expired 8 months prior. Fast Passport Center got me a new one in 6 business days. The courier service picked up my documents directly from Peterborough. I was painting in a lavender field in southern France three weeks later.",
      service: "Passport Renewal",
    },
    {
      name: "James C.",
      location: "Peterborough, NH — Peterborough Players",
      rating: 5,
      text: "Actor with the Peterborough Players who got a last-minute touring opportunity through the UK and Ireland. My passport was valid but within the 6-month window for UK entry. Fast Passport Center renewed it in 5 business days during the summer season. The agent understood theater people work on weird schedules and accommodated everything.",
      service: "Passport Renewal",
    },
    {
      name: "The H Family",
      location: "Dublin, NH",
      rating: 5,
      text: "We're a Monadnock Region family of five heading to Japan for a multi-generational trip with my parents. Grandparents needed renewals, we needed two child passports, and I needed a name change. Fast Passport Center handled every single document in one coordinated batch — all five passports arrived within 9 business days. Flawless experience.",
      service: "Multi-Generational Family Package",
    },
    {
      name: "Rachel T.",
      location: "Hancock, NH",
      rating: 5,
      text: "I run a small artisan business in Hancock and was invited to exhibit at an international craft fair in Florence. My passport had expired without me noticing during a busy production season. Fast Passport Center processed my renewal in 7 business days and the courier picked up directly from my studio. I made it to Florence with my handmade ceramics.",
      service: "Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "ptb-1",
      name: "Peterborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "23 Grove St",
      city: "Peterborough",
      state: "NH",
      zip: "03458",
      phone: "(603) 924-3251",
      hours: "Mon–Fri: 9:00 AM – 4:30 PM",
      distance: "Peterborough — Downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Peterborough and the eastern Monadnock Region. Located on Grove Street near downtown. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "ptb-2",
      name: "Jaffrey Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "36 Main St",
      city: "Jaffrey",
      state: "NH",
      zip: "03452",
      phone: "(603) 532-7784",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Jaffrey — 7 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Neighboring facility in Jaffrey. Good alternative if Peterborough appointments are full. Serves the southern Monadnock Region.",
      icon: "ri-mail-line",
    },
    {
      id: "ptb-3",
      name: "Hillsborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28 Central St",
      city: "Hillsborough",
      state: "NH",
      zip: "03244",
      phone: "(603) 464-3456",
      hours: "Mon–Fri: 9:00 AM – 3:30 PM",
      distance: "Hillsborough — 12 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Hillsborough and the Contoocook Valley north of Peterborough. About 20 minutes from downtown Peterborough.",
      icon: "ri-mail-line",
    },
    {
      id: "ptb-4",
      name: "Keene Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "196 Main St",
      city: "Keene",
      state: "NH",
      zip: "03431",
      phone: "(603) 352-0343",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Keene — 20 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest acceptance facility in Cheshire County. Extended hours and more appointment availability. About 30 minutes west of Peterborough.",
      icon: "ri-mail-line",
    },
    {
      id: "ptb-5",
      name: "Hillsborough County Registry of Deeds",
      type: "County Passport Acceptance",
      address: "329 Mast Rd",
      city: "Goffstown",
      state: "NH",
      zip: "03045",
      phone: "(603) 627-5602",
      hours: "Mon–Fri: 8:30 AM – 4:00 PM",
      distance: "Goffstown — 30 miles east",
      website: "https://www.hcnh.org",
      websiteLabel: "Hillsborough County Government",
      notes: "County-level passport acceptance for all Hillsborough County residents including Peterborough and the eastern Monadnock Region.",
      icon: "ri-government-line",
    },
    {
      id: "ptb-6",
      name: "Antrim Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "55 Main St",
      city: "Antrim",
      state: "NH",
      zip: "03440",
      phone: "(603) 588-2816",
      hours: "Mon–Fri: 9:00 AM – 3:00 PM",
      distance: "Antrim — 8 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Northern option for Peterborough area residents. Smaller facility with limited hours — call ahead for appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Boston Agency", agency: "85 miles — 1 hour 30 minute drive via NH-101 and I-93 plus Boston traffic", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-palette-line",
      title: "MacDowell Artists Delaying Passport Renewal Until Residency Acceptance",
      description: "MacDowell Colony fellows and other artist residency recipients often wait until acceptance before checking their passport. International residencies frequently require travel within weeks of notification, creating a compressed timeline. Artists should keep their passports current at all times — residencies can materialize quickly and passport processing cannot be rushed in the final days before departure.",
    },
    {
      icon: "ri-movie-line",
      title: "Peterborough Players and Touring Artists Not Maintaining Current Passports Year-Round",
      description: "Performing artists at the Peterborough Players and across the Monadnock Region's vibrant theater scene often receive international touring opportunities on short notice. An expired passport means missed opportunities. Professional artists should treat passport renewal as part of their annual career maintenance — always keep it current, even without specific travel plans.",
    },
    {
      icon: "ri-image-line",
      title: "Independent Photographers in Peterborough Using Non-Compliant Passport Photos",
      description: "Peterborough has several independent photography studios and is home to professional photographers. However, passport photos have specific technical requirements — exact 2x2 inches, pure white background, specific head size, no glasses. Even professional photographers unfamiliar with State Department specs may produce technically excellent but non-compliant photos that trigger rejection.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking Visa Requirements for Artist Residencies and Exhibitions",
      description: "Peterborough's creative community frequently travels internationally for artist residencies, exhibitions, and cultural exchanges. Many countries require special artist or work visas in addition to a valid passport — even for unpaid residencies. The combined passport-plus-visa timeline for artists can run 8–12 weeks. Start both processes simultaneously as early as possible.",
    },
    {
      icon: "ri-calendar-line",
      title: "Booking International Travel During Peak Fall Foliage When Peterborough Acceptance Facilities Are Slammed",
      description: "The Monadnock Region's September–October foliage season brings heavy tourism to Peterborough, Dublin, and surrounding towns. Local passport acceptance facilities book 2–4 weeks out during this period due to increased demand from both residents and visitors. Plan DS-11 appointments well in advance if your travel plans coincide with foliage season.",
    },
    {
      icon: "ri-plane-line",
      title: "Relying on Manchester-Boston Regional Airport (MHT) for All International Connections",
      description: "Peterborough is only about 40 miles from MHT, making it the default airport for many Monadnock Region travelers. But MHT has limited international direct routes. Many international itineraries from MHT require connections through larger hubs, adding 3–6 hours to total travel. For long-haul international flights, consider BOS (Boston Logan) which offers significantly more direct international routes.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Peterborough, Keene, or Manchester." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Boston Passport Agency — 85 miles southeast via NH-101 and I-93." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Boston Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Peterborough or Monadnock Region address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Peterborough from the Boston passport agency?",
      a: "Peterborough is approximately 85 miles from the Boston Passport Agency at the Tip O'Neill Federal Building on Causeway Street — about 1 hour 30 minutes southeast via NH-101 east to I-93 south into Boston. The Boston agency requires pre-scheduled NPIC appointments and does not accept walk-ins.",
    },
    {
      q: "Can I get a passport at the Peterborough Post Office?",
      a: "Yes. The Peterborough Post Office at 23 Grove St is a passport acceptance facility. It accepts DS-11 applications and verifies identity documents. However, it does not process or expedite passports — applications are forwarded to the National Passport Center. Call (603) 924-3251 for an appointment. Hours are Monday through Friday, 9 AM to 4:30 PM.",
    },
    {
      q: "Where can I get passport photos in Peterborough?",
      a: "CVS Pharmacy at 3 Grove St in Peterborough offers passport photo services within walking distance of the post office. For additional photo options, Walgreens in Keene (354 Winchester St, about 20 miles west) and CVS in Hillsborough (345 W Main St, about 12 miles northeast) also provide passport photos meeting 2x2 inch specifications.",
    },
    {
      q: "How fast can I get an emergency passport in Peterborough?",
      a: "Emergency processing for Peterborough residents is typically 24–48 hours via courier to Boston. Peterborough's position along the NH-101 corridor ensures reliable overnight routing to Boston year-round. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "I'm a MacDowell Colony fellow — can Fast Passport Center help with last-minute residency travel?",
      a: "Absolutely. Fast Passport Center regularly helps MacDowell Colony artists and fellows with rush passport processing for international residencies. Our 2–9 business day courier service handles document review, photo verification, and secure submission. We understand the compressed timelines artists face when residencies are awarded.",
    },
    {
      q: "Is there a passport agency closer to Peterborough than Boston?",
      a: "No. The closest passport agency to Peterborough is the Boston Passport Agency — about 85 miles southeast. Keene, Manchester, and Nashua all have passport acceptance facilities but no passport agencies. The only passport agency serving the entire Monadnock Region is in Boston.",
    },
    {
      q: "Can you serve Dublin, Hancock, and the surrounding hill towns?",
      a: "Absolutely. Fast Passport Center serves the entire eastern Monadnock Region including Dublin, Hancock, Sharon, Temple, Greenfield, Bennington, Antrim, Wilton, and New Ipswich. Document pickup is available throughout Hillsborough and Cheshire Counties.",
    },
    {
      q: "Does winter weather affect courier service from Peterborough?",
      a: "Winter courier service from Peterborough is reliable. NH-101 and I-93 are maintained as priority state routes during winter storms, and overnight courier services operate consistently even during New England winter. For winter international travel, we recommend starting the passport process at least 4 weeks before departure.",
    },
    {
      q: "The Peterborough Post Office passport appointments are full — what are my alternatives?",
      a: "If Peterborough is booked, check the Jaffrey Post Office (36 Main St, 7 miles south), Hillsborough Post Office (28 Central St, 12 miles northeast), or Antrim Post Office (55 Main St, 8 miles north). For eligible DS-82 mail-in renewals, skip the acceptance facility entirely and use Fast Passport Center's courier service.",
    },
    {
      q: "What's the closest airport to Peterborough for international flights?",
      a: "Peterborough travelers typically fly from Manchester-Boston Regional Airport (MHT, about 40 miles east), Boston Logan International Airport (BOS, about 85 miles southeast), or Bradley International Airport (BDL, about 85 miles southwest in Connecticut). BOS offers the most international direct routes.",
    },
    {
      q: "I'm a Peterborough Players performer on tour — can you handle my passport while I'm traveling?",
      a: "Yes. Fast Passport Center's courier service is designed for busy professionals who can't always be in one place. Our secure online application and overnight document shipping mean you can initiate the passport process from anywhere. We'll overnight your new passport to wherever you need it — whether that's Peterborough or on the road.",
    },
    {
      q: "How does the passport renewal process work if my name has changed after marriage?",
      a: "If your name has changed due to marriage, divorce, or court order, you may need to apply in person using DS-11 (if the name change occurred within one year of passport issuance and you have documentation) or DS-82 (if passed one year with official documentation). Fast Passport Center's agents review your specific situation and guide you to the correct form — preventing rejection for wrong-form errors.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Peterborough", address: "3 Grove St, Peterborough, NH 03458", hours: "Mon–Sun: 8AM–8PM" },
    { name: "Walgreens — Keene", address: "354 Winchester St, Keene, NH 03431", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Hillsborough", address: "345 W Main St, Hillsborough, NH 03244", hours: "Mon–Sun: 8AM–8PM" },
    { name: "CVS Pharmacy — Keene", address: "7 Ash Brook Rd, Keene, NH 03431", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Keene", address: "149 Emerald St, Keene, NH 03431", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "FedEx Office — Manchester", address: "777 S Willow St, Manchester, NH 03103", hours: "Mon–Fri: 7:30AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "NH DMV — Keene", address: "149 Emerald St, Keene, NH 03431", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Closest DMV to Peterborough. REAL ID, driver's license, and state ID services. About 20 miles west in Keene.", featured: true },
    { name: "NH DMV — Concord", address: "23 Hazen Dr, Concord, NH 03301", hours: "Mon–Fri: 8:00AM–4:30PM", note: "State headquarters DMV with full services. About 40 miles northeast of Peterborough.", featured: false },
    { name: "NH DMV — Manchester", address: "377 S Willow St, Manchester, NH 03103", hours: "Mon–Fri: 8:00AM–4:30PM", note: "Larger DMV with more appointment availability. About 45 miles east of Peterborough.", featured: false },
  ],
  airports: [
    {
      code: "MHT",
      name: "Manchester-Boston Regional Airport",
      distance: "40 miles east of Peterborough",
      address: "1 Airport Rd, Manchester, NH 03103",
      note: "Closest commercial airport and New Hampshire's largest. Domestic and limited international routes. About 50 minutes from Peterborough via NH-101. Most convenient for Monadnock Region travelers.",
      flightUrl: "https://www.flymanchester.com/",
      flightLabel: "MHT Airport",
      featured: true,
    },
    {
      code: "BOS",
      name: "Boston Logan International Airport",
      distance: "85 miles southeast of Peterborough",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "New England's largest international gateway with direct flights worldwide. About 1 hour 30 minutes from Peterborough via NH-101 and I-93. Co-located with the Boston Passport Agency.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "BOS Airport",
      featured: false,
    },
    {
      code: "BDL",
      name: "Bradley International Airport",
      distance: "85 miles southwest of Peterborough",
      address: "11 Schoephoester Rd, Windsor Locks, CT 06096",
      note: "Connecticut's largest airport with domestic and some international routes. About 1 hour 30 minutes from Peterborough via I-91 south. Good alternative for southern Monadnock Region travelers.",
      flightUrl: "https://www.bradleyairport.com/",
      flightLabel: "BDL Airport",
      featured: false,
    },
    {
      code: "PVD",
      name: "Rhode Island T.F. Green International Airport",
      distance: "95 miles south of Peterborough",
      address: "2000 Post Rd, Warwick, RI 02886",
      note: "Growing international gateway with convenient connections. About 1 hour 45 minutes from Peterborough via I-190 and I-295.",
      flightUrl: "https://www.flyri.com/",
      flightLabel: "PVD Airport",
      featured: false,
    },
  ],
};